/**
 * Loss Ratio Analysis tab (OR-1176) — premium vs incurred by policy year.
 *
 * 100% backend (jun-16): read-only, fed by the `/loss-runs/summary`
 * breakdown via `useLossRuns` (same source as Annual Loss Summary).
 * Nothing is editable and nothing is computed FE-side.
 *
 * Column → backend field:
 *   Policy Year                    ← report_year
 *   Premium                        ← on_leveled_premium
 *   Total Incurred                 ← incurred
 *   Loss Ratio                     ← on_leveled_loss_ratio
 *   Selected Premium               ← selected_premium
 *   Adjusted XS the SIR Choosen    ← adjusted_excess
 *   Adjusted Loss Ratio Xs the SIR ← adjusted_loss_ratio
 *   Selected Year to Weight        ← hardcoded "1" (Linda: "default to 1
 *                                     for MVP, not editable"; confirmed by
 *                                     Henry, Slack jun-16 — no BE field)
 *
 * Most actuarial columns currently come back null (premium not loaded
 * yet) and render "—". Flagged to Henry (orion-rewiring-questions.md).
 */
import { Table } from "@tricura/atlas-ui";
import { useLossRuns } from "../../../../hooks/use-loss-runs";
import { displayMoney, num } from "../loss-run-calc";
import { displayPercent } from "../loss-summary-calc";

const W = {
    policyYear: 120,
    premium: 150,
    totalIncurred: 150,
    lossRatio: 120,
    selectedPremium: 160,
    adjustedXsSir: 210,
    adjustedLossRatioXsSir: 220,
    selectedYearToWeight: 190,
} as const;

const fixed = (px: number) => ({ width: px, minWidth: px });
const READONLY_CELL = "text-secondary text-sm tabular-nums";

/** Column definitions (OR-1117) — surfaced as header tooltips so the
 *  actuarial columns aren't mistaken for raw values. "Premium" especially:
 *  it's the on-leveled premium that factors in acuity (Henry, jun-22). */
const COL_TOOLTIPS = {
    premium: "On-leveled premium — the year's premium re-stated to current levels; factors in acuity (equivalency × rate per bed).",
    lossRatio: "Total incurred ÷ on-leveled premium (historical basis).",
    selectedPremium: "New-policy premium selected for the analysis (flat across years).",
    adjustedXsSir: "Per-claim losses above the chosen SIR for the year.",
    adjustedLossRatioXsSir: "Adjusted excess ÷ selected premium (current basis).",
    selectedYearToWeight: "Weight applied to the year in the blend (MVP: fixed at 1).",
} as const;

/** Money from a Decimal-as-string — "" (backend null) → "—". */
function money(s: string): string {
    return s.trim() === "" ? "—" : displayMoney(num(s));
}

/** Ratio-as-string → whole percent. "" (backend null) → "—". */
function percent(s: string): string {
    return s.trim() === "" ? "—" : displayPercent(num(s));
}

interface LossRatioAnalysisTableProps {
    submissionId: string | undefined;
}

export function LossRatioAnalysisTable({ submissionId }: LossRatioAnalysisTableProps) {
    const { state } = useLossRuns(submissionId);
    const summary = state.status === "ready" ? state.summary : null;
    const rows = summary?.rows ?? [];

    return (
        <div className="relative flex flex-col overflow-hidden lg:min-h-0 lg:flex-1">
            <Table
                aria-label="Loss Ratio Analysis"
                wrapperClassName="overflow-auto border-b border-secondary lg:min-h-0 lg:flex-1"
                className="[&_tbody_tr:last-child]:border-b-0!"
            >
                <Table.Header className="bg-secondary sticky top-0 z-20">
                    {/* Column tooltips on for the actuarial columns (OR-1117 defs,
                      * Henry jun-22) so "Premium" etc. aren't read as raw values. */}
                    <Table.Head id="policyYear" label="Policy Year" isRowHeader style={fixed(W.policyYear)} />
                    <Table.Head id="premium" label="Premium" tooltip={COL_TOOLTIPS.premium} style={fixed(W.premium)} />
                    <Table.Head id="totalIncurred" label="Total Incurred" style={fixed(W.totalIncurred)} />
                    <Table.Head id="lossRatio" label="Loss Ratio" tooltip={COL_TOOLTIPS.lossRatio} style={fixed(W.lossRatio)} />
                    <Table.Head id="selectedPremium" label="Selected Premium" tooltip={COL_TOOLTIPS.selectedPremium} style={fixed(W.selectedPremium)} />
                    <Table.Head id="adjustedXsSir" label="Adjusted XS the SIR Choosen" tooltip={COL_TOOLTIPS.adjustedXsSir} style={fixed(W.adjustedXsSir)} />
                    <Table.Head id="adjustedLossRatioXsSir" label="Adjusted Loss Ratio Xs the SIR" tooltip={COL_TOOLTIPS.adjustedLossRatioXsSir} style={fixed(W.adjustedLossRatioXsSir)} />
                    <Table.Head id="selectedYearToWeight" label="Selected Year to Weight" tooltip={COL_TOOLTIPS.selectedYearToWeight} style={fixed(W.selectedYearToWeight)} />
                </Table.Header>
                <Table.Body>
                    {rows.map((r) => (
                        <Table.Row id={String(r.reportYear)} key={r.reportYear}>
                            <Table.Cell style={fixed(W.policyYear)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-loss-ratio-analysis-tab-policy-year">{r.reportYear}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.premium)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-loss-ratio-analysis-tab-premium">{money(r.onLeveledPremium)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.totalIncurred)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-loss-ratio-analysis-tab-total-incurred">{money(r.incurred)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.lossRatio)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-loss-ratio-analysis-tab-loss-ratio">{percent(r.onLeveledLossRatio)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.selectedPremium)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-loss-ratio-analysis-tab-selected-premium">{money(r.selectedPremium)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.adjustedXsSir)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-loss-ratio-analysis-tab-adjusted-excess">{money(r.adjustedExcess)}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.adjustedLossRatioXsSir)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-loss-ratio-analysis-tab-adjusted-loss-ratio">{percent(r.adjustedLossRatio)}</span>
                            </Table.Cell>
                            {/* Selected Year to Weight = 1 for MVP, not editable
                              * (Linda's Figma note; Henry confirmed no BE field). */}
                            <Table.Cell style={fixed(W.selectedYearToWeight)}>
                                <span className={READONLY_CELL} data-autotest="loss-runs-loss-ratio-analysis-tab-selected-year-to-weight">1</span>
                            </Table.Cell>
                        </Table.Row>
                    ))}

                    {rows.length > 0 && (
                        <Table.Row id="__totals__" className="bg-secondary font-semibold sticky bottom-0 z-10">
                            <Table.Cell className="text-quaternary text-xs uppercase tracking-wider" style={fixed(W.policyYear)}>
                                {rows.length} years
                            </Table.Cell>
                            <Table.Cell style={fixed(W.premium)} />
                            {/* Total Incurred is the only backend-totaled column. */}
                            <Table.Cell style={fixed(W.totalIncurred)}>
                                <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-loss-ratio-analysis-tab-total-incurred">{money(summary?.totalPaidAndReserved ?? "")}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.lossRatio)} />
                            <Table.Cell style={fixed(W.selectedPremium)} />
                            <Table.Cell style={fixed(W.adjustedXsSir)} />
                            <Table.Cell style={fixed(W.adjustedLossRatioXsSir)}>
                                <span className="text-primary text-sm tabular-nums" data-autotest="loss-runs-loss-ratio-analysis-tab-total-adjusted-loss-ratio">{percent(summary?.totalAdjustedLossRatio ?? "")}</span>
                            </Table.Cell>
                            <Table.Cell style={fixed(W.selectedYearToWeight)} />
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>
        </div>
    );
}
